<script setup>
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';
import { useAccount } from 'dashboard/composables/useAccount';
import { dynamicTime } from 'shared/helpers/timeHelper';
import Avatar from 'dashboard/components-next/avatar/Avatar.vue';

const props = defineProps({
  conversation: {
    type: Object,
    required: true,
  },
});

const { t } = useI18n();
const { accountScopedRoute } = useAccount();

const contact = computed(() => props.conversation.meta?.sender || {});
const assignee = computed(() => props.conversation.meta?.assignee);
const inbox = computed(() => props.conversation.meta?.channel || '');

const lastMessage = computed(() => {
  const content = props.conversation.last_non_activity_message?.content;
  return content || t('KANBAN.CARD.NO_CONTENT');
});

const conversationRoute = computed(() =>
  accountScopedRoute('inbox_conversation', {
    conversation_id: props.conversation.id,
  })
);

const updatedAt = computed(() => {
  const timestamp = props.conversation.timestamp;
  return timestamp ? dynamicTime(timestamp) : '';
});
</script>

<template>
  <router-link
    :to="conversationRoute"
    class="flex flex-col gap-2 p-3 rounded-lg bg-n-solid-2 outline outline-1 outline-n-container hover:outline-n-brand transition-all cursor-grab active:cursor-grabbing"
  >
    <div class="flex items-center justify-between gap-2 min-w-0">
      <div class="flex items-center gap-2 min-w-0">
        <Avatar :name="contact.name || t('KANBAN.CARD.UNKNOWN_CONTACT')" :src="contact.thumbnail" :size="24" />
        <span class="text-sm font-medium truncate text-n-slate-12">
          {{ contact.name || t('KANBAN.CARD.UNKNOWN_CONTACT') }}
        </span>
      </div>
      <span class="text-xs text-n-slate-10 flex-shrink-0">#{{ conversation.id }}</span>
    </div>

    <p class="text-xs text-n-slate-11 line-clamp-2 m-0">
      {{ lastMessage }}
    </p>

    <div class="flex items-center justify-between gap-2 pt-1">
      <span
        v-if="inbox"
        class="text-[11px] px-1.5 py-0.5 rounded bg-n-alpha-2 text-n-slate-11 truncate max-w-[45%]"
      >
        {{ inbox }}
      </span>
      <div class="flex items-center gap-1 ml-auto">
        <span v-if="updatedAt" class="text-[11px] text-n-slate-10">
          {{ updatedAt }}
        </span>
        <Avatar
          v-if="assignee?.name"
          :name="assignee.name"
          :src="assignee.thumbnail"
          :size="18"
          rounded-full
        />
      </div>
    </div>
  </router-link>
</template>
